import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { PhotoScore, EditRecipe, EditIntensity, DimensionKey } from '@frame/scoring-types';
import { TIER_ENTITLEMENTS, SubscriptionTier } from '@frame/scoring-types';
import { EditSlider } from '../components/editor/EditSlider';
import { BeforeAfter } from '../components/editor/BeforeAfter';
import { PhotoStrip } from '../components/editor/PhotoStrip';
import { AIRecipeCard } from '../components/editor/AIRecipeCard';
import {
  applyEdits, exportCanvas, loadImageData, recipeToEditAdjustment,
  DEFAULT_EDIT_STATE, isDefaultState,
  type EditState,
} from '../services/imageEditor';

const C = {
  bg: '#0f172a', text: '#f8fafc',
  teal: '#50B8A8', tealHover: '#3D9E8F', mid: '#3B8CB8',
  blue: '#5186B0',
  navy: '#1A2E44', navySoft: '#2D4A66',
  surface: '#1e293b', secondary: '#94a3b8', divider: '#334155',
  error: 'rgba(239, 68, 68, 0.7)', white: '#FFFFFF',
  muted: '#64748b', success: '#059669',
} as const;

function scoreColor(score: number): string {
  if (score < 3) return '#EF4444';
  if (score < 5) return '#F59E0B';
  if (score < 7) return '#50B8A8';
  return '#059669';
}

const INTENSITIES: { id: EditIntensity; label: string; range: [number, number]; tier: 'free' | 'silver' | 'gold' }[] = [
  { id: 'subtle', label: 'Subtle', range: [0.10, 0.25], tier: 'silver' },
  { id: 'lowkey', label: 'Lowkey', range: [0.25, 0.50], tier: 'silver' },
  { id: 'balanced', label: 'Balanced', range: [0.50, 0.75], tier: 'gold' },
  { id: 'highkey', label: 'Highkey', range: [0.75, 1.00], tier: 'gold' },
];

function generateEditRecipes(photo: PhotoScore, intensity: EditIntensity): EditRecipe[] {
  const strengthUpper = INTENSITIES.find(i => i.id === intensity)?.range[1] ?? 0.5;
  const recipes: EditRecipe[] = [];

  const dims: { key: DimensionKey; score: number }[] = [];
  if (photo.dimensions.portraitComposition !== null) {
    dims.push({ key: 'portraitComposition', score: photo.dimensions.portraitComposition });
  }
  dims.push({ key: 'lighting', score: photo.dimensions.lighting });
  dims.push({ key: 'composition', score: photo.dimensions.composition });
  dims.push({ key: 'sceneInterest', score: photo.dimensions.sceneInterestSpiked });
  dims.sort((a, b) => a.score - b.score);

  for (const { key, score } of dims) {
    const gap = 10.0 - score;
    const editStrength = Math.min(strengthUpper, gap / 10.0);
    if (editStrength < 0.05) continue;

    switch (key) {
      case 'lighting':
        if (score < 5) {
          recipes.push({ dimension: key, editName: 'Increase exposure', intensity: editStrength, description: 'Brighten the image to improve lighting balance' });
        }
        recipes.push({ dimension: key, editName: 'Boost contrast', intensity: editStrength * 0.7, description: 'Add contrast to increase dynamic range and depth' });
        if (score < 6) {
          recipes.push({ dimension: key, editName: 'Warm color temperature', intensity: editStrength * 0.4, description: 'Slight warmth shift for more inviting tones' });
        }
        break;
      case 'composition':
        recipes.push({ dimension: key, editName: 'Add vignette', intensity: editStrength * 0.5, description: 'Subtle vignette to draw focus to center' });
        break;
      case 'sceneInterest':
        recipes.push({ dimension: key, editName: 'Increase saturation', intensity: editStrength * 0.5, description: 'Boost color vibrancy for visual impact' });
        recipes.push({ dimension: key, editName: 'Add sharpness', intensity: editStrength * 0.4, description: 'Sharpen details for added clarity' });
        break;
      case 'portraitComposition':
        recipes.push({ dimension: key, editName: 'Warm color temperature', intensity: editStrength * 0.3, description: 'Warm skin tones for a more flattering look' });
        recipes.push({ dimension: key, editName: 'Add sharpness', intensity: editStrength * 0.3, description: 'Sharpen facial details' });
        break;
    }
  }
  return recipes;
}

export default function EditPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Photos from router state (passed from Home)
  const passedPhotos = (location.state as { scoredPhotos?: PhotoScore[] })?.scoredPhotos ?? [];

  const [scoredPhotos] = useState<PhotoScore[]>(passedPhotos);
  const [unratedPhotos, setUnratedPhotos] = useState<{ file: File; url: string }[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(passedPhotos[0]?.id ?? null);
  const [editState, setEditState] = useState<EditState>({ ...DEFAULT_EDIT_STATE });
  const [originalData, setOriginalData] = useState<ImageData | null>(null);
  const [originalUrl, setOriginalUrl] = useState<string>('');
  const [imgDims, setImgDims] = useState({ w: 0, h: 0 });
  const [loading, setLoading] = useState(false);
  const [aiIntensity, setAiIntensity] = useState<EditIntensity>('lowkey');
  const [showPanel, setShowPanel] = useState<'manual' | 'ai'>('manual');

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const userTier = SubscriptionTier.Free; // TODO: wire to real entitlement
  const entitlements = TIER_ENTITLEMENTS[userTier];

  // Load selected photo into canvas
  useEffect(() => {
    if (!selectedId) return;

    const loadPhoto = async () => {
      setLoading(true);
      try {
        let file: File | null = null;
        let thumbUrl = '';

        const scored = scoredPhotos.find(p => p.id === selectedId);
        if (scored?.thumbnailUri) {
          // Fetch from blob URL
          const resp = await fetch(scored.thumbnailUri);
          const blob = await resp.blob();
          file = new File([blob], scored.assetIdentifier, { type: blob.type });
          thumbUrl = scored.thumbnailUri;
        }

        const unratedIdx = selectedId.startsWith('unrated-') ? parseInt(selectedId.replace('unrated-', '')) : -1;
        if (unratedIdx >= 0 && unratedPhotos[unratedIdx]) {
          file = unratedPhotos[unratedIdx].file;
          thumbUrl = unratedPhotos[unratedIdx].url;
        }

        if (!file) { setLoading(false); return; }

        const { imageData, width, height } = await loadImageData(file);
        setOriginalData(imageData);
        setOriginalUrl(thumbUrl);
        setImgDims({ w: width, h: height });
        setEditState({ ...DEFAULT_EDIT_STATE });

        // Initial render
        if (canvasRef.current) {
          applyEdits(canvasRef.current, imageData, DEFAULT_EDIT_STATE);
        }
      } catch (err) {
        console.error('Failed to load photo:', err);
      }
      setLoading(false);
    };

    loadPhoto();
  }, [selectedId, scoredPhotos, unratedPhotos]);

  // Re-apply edits when state changes
  useEffect(() => {
    if (!originalData || !canvasRef.current) return;
    requestAnimationFrame(() => {
      applyEdits(canvasRef.current!, originalData, editState);
    });
  }, [editState, originalData]);

  const handleSliderChange = useCallback((key: keyof EditState, value: number) => {
    setEditState(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleReset = useCallback(() => {
    setEditState({ ...DEFAULT_EDIT_STATE });
  }, []);

  const handleExport = useCallback(async () => {
    if (!canvasRef.current) return;
    const watermark = userTier === SubscriptionTier.Free;
    const blob = await exportCanvas(canvasRef.current, watermark);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `frame-edited-${Date.now()}.jpg`;
    a.click();
    URL.revokeObjectURL(url);
  }, [userTier]);

  const handleAddUnrated = useCallback((files: FileList) => {
    const newPhotos = Array.from(files).map(f => ({
      file: f,
      url: URL.createObjectURL(f),
    }));
    setUnratedPhotos(prev => [...prev, ...newPhotos]);
    if (!selectedId && newPhotos.length > 0) {
      setSelectedId('unrated-0');
    }
  }, [selectedId]);

  const handleApplyRecipe = useCallback((recipe: EditRecipe) => {
    const adj = recipeToEditAdjustment(recipe.editName, recipe.intensity);
    setEditState(prev => {
      const next = { ...prev };
      for (const [key, val] of Object.entries(adj)) {
        (next as any)[key] = Math.max(-100, Math.min(100, ((prev as any)[key] ?? 0) + (val as number)));
      }
      return next;
    });
  }, []);

  const handleRotate = useCallback(() => {
    setEditState(prev => ({
      ...prev,
      rotation: ((prev.rotation + 90) % 360) as 0 | 90 | 180 | 270,
    }));
  }, []);

  const selectedPhoto = scoredPhotos.find(p => p.id === selectedId);
  const recipes = selectedPhoto ? generateEditRecipes(selectedPhoto, aiIntensity) : [];
  const canUseAI = entitlements.editIntensities.length > 1;
  const hasEdits = !isDefaultState(editState);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0f172a 0%, #0c1220 40%, #0e1528 100%)',
      fontFamily: "'Inter', system-ui, sans-serif",
    }}>
      {/* Header */}
      <header style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: 'max(16px, env(safe-area-inset-top, 16px)) clamp(12px, 3vw, 32px) 12px',
        position: 'sticky', top: 0, zIndex: 50,
        backgroundColor: 'rgba(15,23,42,0.9)',
        borderBottom: `1px solid ${C.divider}`,
        backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
      }}>
        <button
          onClick={() => navigate('/')}
          style={{
            background: 'none', border: 'none', color: C.teal,
            fontSize: 15, fontWeight: 500, cursor: 'pointer',
            fontFamily: "'Inter', system-ui, sans-serif",
            padding: '6px 4px', minHeight: 44,
          }}
        >
          Back
        </button>
        <h1 style={{
          fontSize: 'clamp(18px, 3.5vw, 22px)', fontWeight: 700, color: C.text,
          letterSpacing: 2, margin: 0,
          fontFamily: "'Inter', system-ui, sans-serif",
        }}>
          EDIT
        </h1>
        <div style={{ display: 'flex', gap: 8 }}>
          {hasEdits && (
            <button
              onClick={handleReset}
              style={{
                padding: '8px 14px', borderRadius: 8,
                border: `1px solid ${C.divider}`, background: 'transparent',
                color: C.secondary, fontSize: 13, fontWeight: 500, cursor: 'pointer',
              }}
            >
              Reset
            </button>
          )}
          <button
            onClick={handleExport}
            disabled={!originalData}
            style={{
              padding: '8px 18px', borderRadius: 8, border: 'none',
              background: originalData ? `linear-gradient(135deg, ${C.teal}, ${C.blue})` : C.muted,
              color: '#fff', fontSize: 13, fontWeight: 600, cursor: originalData ? 'pointer' : 'not-allowed',
            }}
          >
            Export
          </button>
        </div>
      </header>

      <main style={{ maxWidth: 900, margin: '0 auto', padding: '0 clamp(12px, 3vw, 24px) 32px' }}>
        {/* Photo strip */}
        <div style={{
          background: C.surface, borderRadius: 14, padding: '8px 12px',
          margin: '16px 0',
        }}>
          <PhotoStrip
            scoredPhotos={scoredPhotos}
            unratedPhotos={unratedPhotos}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onAddUnrated={handleAddUnrated}
          />
        </div>

        {/* Empty state */}
        {!selectedId && scoredPhotos.length === 0 && unratedPhotos.length === 0 && (
          <div style={{
            textAlign: 'center', padding: '60px 20px',
            color: C.secondary, fontSize: 15,
          }}>
            <p style={{ fontSize: 18, fontWeight: 600, color: C.text, marginBottom: 8 }}>No photos to edit</p>
            <p style={{ marginBottom: 20 }}>Score some photos first, or add unrated photos using the + button above.</p>
            <button
              onClick={() => navigate('/')}
              style={{
                padding: '10px 24px', borderRadius: 10, border: 'none',
                background: `linear-gradient(135deg, ${C.teal}, ${C.blue})`,
                color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Go to Scoring
            </button>
          </div>
        )}

        {/* Editor layout */}
        {selectedId && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth > 768 ? '1fr 280px' : '1fr',
            gap: 16, alignItems: 'start',
          }}>
            {/* Canvas area */}
            <div style={{
              background: C.surface, borderRadius: 16, padding: 16,
              display: 'flex', flexDirection: 'column', alignItems: 'center',
            }}>
              {loading ? (
                <div style={{
                  height: 300, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', color: C.secondary,
                }}>
                  Loading...
                </div>
              ) : originalData ? (
                <BeforeAfter
                  originalUrl={originalUrl}
                  editedCanvasRef={canvasRef}
                  width={imgDims.w}
                  height={imgDims.h}
                />
              ) : (
                <div style={{
                  height: 300, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', color: C.secondary,
                }}>
                  Select a photo to edit
                </div>
              )}

              {/* Score badge if scored */}
              {selectedPhoto && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  marginTop: 12, padding: '8px 16px',
                  background: C.navySoft, borderRadius: 10,
                }}>
                  <span style={{
                    fontSize: 22, fontWeight: 800,
                    color: scoreColor(selectedPhoto.compositeScore),
                  }}>
                    {selectedPhoto.compositeScore.toFixed(1)}
                  </span>
                  <div style={{ fontSize: 12, color: C.secondary, lineHeight: 1.4 }}>
                    <div>Portrait: {selectedPhoto.dimensions.portraitComposition?.toFixed(1) ?? 'N/A'}</div>
                    <div>Lighting: {selectedPhoto.dimensions.lighting.toFixed(1)}</div>
                    <div>Comp: {selectedPhoto.dimensions.composition.toFixed(1)}</div>
                    <div>Scene: {selectedPhoto.dimensions.sceneInterest.toFixed(1)}</div>
                  </div>
                </div>
              )}

              {/* Rotate button */}
              {originalData && (
                <button
                  onClick={handleRotate}
                  style={{
                    marginTop: 8, padding: '6px 16px', borderRadius: 8,
                    border: `1px solid ${C.divider}`, background: 'transparent',
                    color: C.secondary, fontSize: 12, cursor: 'pointer',
                  }}
                >
                  Rotate 90
                </button>
              )}
            </div>

            {/* Controls panel */}
            <div style={{
              background: C.surface, borderRadius: 16, padding: 16,
            }}>
              {/* Tab toggle */}
              <div style={{
                display: 'flex', gap: 4, marginBottom: 16,
                background: C.navySoft, borderRadius: 10, padding: 3,
              }}>
                <button
                  onClick={() => setShowPanel('manual')}
                  style={{
                    flex: 1, padding: '8px 0', borderRadius: 8,
                    border: 'none', fontSize: 13, fontWeight: 600,
                    cursor: 'pointer',
                    background: showPanel === 'manual' ? `linear-gradient(135deg, ${C.teal}, ${C.blue})` : 'transparent',
                    color: showPanel === 'manual' ? '#fff' : C.secondary,
                  }}
                >
                  Manual
                </button>
                <button
                  onClick={() => setShowPanel('ai')}
                  style={{
                    flex: 1, padding: '8px 0', borderRadius: 8,
                    border: 'none', fontSize: 13, fontWeight: 600,
                    cursor: 'pointer',
                    background: showPanel === 'ai' ? `linear-gradient(135deg, ${C.teal}, ${C.blue})` : 'transparent',
                    color: showPanel === 'ai' ? '#fff' : C.secondary,
                  }}
                >
                  AI Assist
                </button>
              </div>

              {showPanel === 'manual' && (
                <>
                  <EditSlider label="Brightness" value={editState.brightness} min={-100} max={100} onChange={v => handleSliderChange('brightness', v)} />
                  <EditSlider label="Contrast" value={editState.contrast} min={-100} max={100} onChange={v => handleSliderChange('contrast', v)} />
                  <EditSlider label="Saturation" value={editState.saturation} min={-100} max={100} onChange={v => handleSliderChange('saturation', v)} />
                  <EditSlider label="Warmth" value={editState.warmth} min={-100} max={100} onChange={v => handleSliderChange('warmth', v)} />
                  <EditSlider label="Sharpness" value={editState.sharpness} min={0} max={100} onChange={v => handleSliderChange('sharpness', v)} />
                  <EditSlider label="Vignette" value={editState.vignette} min={0} max={100} onChange={v => handleSliderChange('vignette', v)} />
                </>
              )}

              {showPanel === 'ai' && (
                <>
                  {!selectedPhoto ? (
                    <p style={{ fontSize: 13, color: C.secondary, textAlign: 'center', padding: 20 }}>
                      AI recipes require a scored photo. Score this photo first.
                    </p>
                  ) : (
                    <>
                      {/* Intensity selector */}
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 12, color: C.secondary, marginBottom: 6 }}>Edit Intensity</div>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {INTENSITIES.map(int => {
                            const allowed = entitlements.editIntensities.includes(int.id);
                            return (
                              <button
                                key={int.id}
                                onClick={() => allowed && setAiIntensity(int.id)}
                                style={{
                                  flex: 1, padding: '6px 0', borderRadius: 6,
                                  border: aiIntensity === int.id ? `1px solid ${C.teal}` : `1px solid ${C.divider}`,
                                  background: aiIntensity === int.id ? 'rgba(80,184,168,0.15)' : 'transparent',
                                  color: allowed ? C.text : C.muted,
                                  fontSize: 11, fontWeight: 500,
                                  cursor: allowed ? 'pointer' : 'not-allowed',
                                  opacity: allowed ? 1 : 0.5,
                                }}
                              >
                                {int.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Recipe cards */}
                      {recipes.length > 0 ? (
                        recipes.map((r, i) => (
                          <AIRecipeCard
                            key={i}
                            recipe={r}
                            onApply={handleApplyRecipe}
                            locked={!canUseAI}
                          />
                        ))
                      ) : (
                        <p style={{ fontSize: 13, color: C.secondary, textAlign: 'center', padding: 20 }}>
                          This photo scores well! No significant improvements suggested.
                        </p>
                      )}

                      {!canUseAI && (
                        <div style={{
                          marginTop: 8, padding: '10px 14px',
                          background: 'rgba(80,184,168,0.08)',
                          borderRadius: 10, border: `1px solid rgba(80,184,168,0.15)`,
                        }}>
                          <p style={{ fontSize: 12, color: C.teal, margin: 0, fontWeight: 500 }}>
                            Upgrade to Silver or Gold to unlock AI-assisted editing
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
